import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback } from 'react';
import { DEFAULT_BREAK_TYPES } from './ClockOutPromptModal.types';
import { clockTokens } from './utils';
/**
 * ClockOutPromptModal - Web Implementation
 *
 * A modal that prompts users when they click clock out to either:
 * 1. End their shift completely
 * 2. Take a break (with break type selection)
 */
export const ClockOutPromptModal = ({ visible, onClose, onEndShift, onStartBreak, breakTypes, isLoading = false, elapsedTimeDisplay, testID = 'clock-out-prompt-modal', }) => {
    const [selectedAction, setSelectedAction] = useState(null);
    const [selectedBreakType, setSelectedBreakType] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    // Use provided break types or default
    const availableBreakTypes = breakTypes && breakTypes.length > 0 ? breakTypes : DEFAULT_BREAK_TYPES;
    const handleClose = useCallback(() => {
        if (isProcessing)
            return;
        setSelectedAction(null);
        setSelectedBreakType(null);
        onClose();
    }, [isProcessing, onClose]);
    const handleActionSelect = useCallback((action) => {
        setSelectedAction(action);
        if (action === 'end_shift') {
            setSelectedBreakType(null);
        }
    }, []);
    const handleConfirm = useCallback(async () => {
        if (isProcessing || !selectedAction)
            return;
        setIsProcessing(true);
        try {
            if (selectedAction === 'end_shift') {
                await onEndShift();
            }
            else if (selectedAction === 'take_break') {
                // Find by name since id may be undefined for default break types
                const breakType = availableBreakTypes.find((bt) => bt.name === selectedBreakType) || availableBreakTypes[0];
                await onStartBreak(breakType.id, breakType.name);
            }
            handleClose();
        }
        catch (error) {
            console.error('Clock out action failed:', error);
        }
        finally {
            setIsProcessing(false);
        }
    }, [isProcessing, selectedAction, selectedBreakType, availableBreakTypes, onEndShift, onStartBreak, handleClose]);
    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Escape') {
            handleClose();
        }
    }, [handleClose]);
    if (!visible)
        return null;
    const showLoading = isLoading || isProcessing;
    // Check if confirm button should be enabled
    const canConfirm = selectedAction === 'end_shift' || (selectedAction === 'take_break' && (selectedBreakType || availableBreakTypes.length === 1));
    return (_jsx("div", { "data-testid": testID, style: {
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
        }, onClick: handleClose, onKeyDown: handleKeyDown, role: "dialog", "aria-modal": "true", "aria-labelledby": "clock-out-modal-title", children: _jsxs("div", { style: {
                backgroundColor: '#1F2937',
                borderRadius: 12,
                padding: 24,
                width: 'min(400px, 90vw)',
                maxHeight: '90vh',
                overflowY: 'auto',
                border: '1px solid #374151',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
            }, onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { style: {
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 20,
                    }, children: [_jsx("h2", { id: "clock-out-modal-title", style: {
                                margin: 0,
                                fontSize: 17,
                                fontWeight: 600,
                                color: '#FFFFFF',
                            }, children: "Check Out" }), _jsx("button", { onClick: handleClose, disabled: showLoading, style: {
                                background: 'none',
                                border: 'none',
                                color: '#9CA3AF',
                                fontSize: 22,
                                cursor: showLoading ? 'not-allowed' : 'pointer',
                                padding: 4,
                                lineHeight: 1,
                                borderRadius: 4,
                            }, "aria-label": "Close modal", children: "\u00D7" })] }), elapsedTimeDisplay && (_jsxs("div", { style: {
                        textAlign: 'center',
                        marginBottom: 20,
                        padding: 12,
                        backgroundColor: '#111827',
                        borderRadius: 8,
                    }, children: [_jsx("div", { style: { fontSize: 12, color: '#9CA3AF', marginBottom: 4 }, children: "Time Worked" }), _jsx("div", { style: { fontSize: 22, fontWeight: 600, color: clockTokens.colors.active.text }, children: elapsedTimeDisplay })] })), _jsxs("div", { style: { marginBottom: 20 }, children: [_jsx("div", { style: { fontSize: 13, color: '#D1D5DB', marginBottom: 12 }, children: "What would you like to do?" }), _jsxs("button", { onClick: () => handleActionSelect('end_shift'), disabled: showLoading, style: {
                                width: '100%',
                                padding: 16,
                                marginBottom: 8,
                                borderRadius: 8,
                                border: `2px solid ${selectedAction === 'end_shift' ? '#3B82F6' : '#374151'}`,
                                backgroundColor: selectedAction === 'end_shift' ? 'rgba(59, 130, 246, 0.1)' : '#111827',
                                cursor: showLoading ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                transition: 'all 150ms ease-out',
                            }, children: [_jsx("div", { style: {
                                        width: 32,
                                        height: 32,
                                        borderRadius: 6,
                                        backgroundColor: '#DC2626',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }, children: _jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "white", children: _jsx("rect", { x: "6", y: "6", width: "12", height: "12", rx: "1" }) }) }), _jsxs("div", { style: { textAlign: 'left' }, children: [_jsx("div", { style: { fontSize: 15, fontWeight: 600, color: '#FFFFFF' }, children: "End Shift" }), _jsx("div", { style: { fontSize: 12, color: '#9CA3AF' }, children: "Check out and end your shift" })] })] }), _jsxs("button", { onClick: () => handleActionSelect('take_break'), disabled: showLoading, style: {
                                width: '100%',
                                padding: 16,
                                borderRadius: 8,
                                border: `2px solid ${selectedAction === 'take_break' ? '#3B82F6' : '#374151'}`,
                                backgroundColor: selectedAction === 'take_break' ? 'rgba(59, 130, 246, 0.1)' : '#111827',
                                cursor: showLoading ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                transition: 'all 150ms ease-out',
                            }, children: [_jsx("div", { style: {
                                        width: 32,
                                        height: 32,
                                        borderRadius: 6,
                                        backgroundColor: '#F59E0B',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }, children: _jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "white", children: [_jsx("rect", { x: "6", y: "4", width: "4", height: "16", rx: "1" }), _jsx("rect", { x: "14", y: "4", width: "4", height: "16", rx: "1" })] }) }), _jsxs("div", { style: { textAlign: 'left' }, children: [_jsx("div", { style: { fontSize: 15, fontWeight: 600, color: '#FFFFFF' }, children: "Take a Break" }), _jsx("div", { style: { fontSize: 12, color: '#9CA3AF' }, children: "Pause your session for a break" })] })] })] }), selectedAction === 'take_break' && availableBreakTypes.length > 1 && (_jsxs("div", { style: {
                        marginBottom: 20,
                        padding: 16,
                        backgroundColor: '#111827',
                        borderRadius: 8,
                    }, children: [_jsx("div", { style: { fontSize: 13, color: '#D1D5DB', marginBottom: 12 }, children: "Select break type:" }), _jsx("div", { style: { display: 'flex', flexWrap: 'wrap', gap: 8 }, children: availableBreakTypes.map((breakType) => (_jsxs("button", { onClick: () => setSelectedBreakType(breakType.name), disabled: showLoading, style: {
                                    padding: '8px 16px',
                                    borderRadius: 20,
                                    border: `1px solid ${selectedBreakType === breakType.name ? '#F59E0B' : '#374151'}`,
                                    backgroundColor: selectedBreakType === breakType.name ? 'rgba(245, 158, 11, 0.2)' : 'transparent',
                                    color: selectedBreakType === breakType.name ? '#F59E0B' : '#D1D5DB',
                                    cursor: showLoading ? 'not-allowed' : 'pointer',
                                    fontSize: 13,
                                    fontWeight: 500,
                                    transition: 'all 150ms ease-out',
                                }, children: [breakType.name, breakType.is_paid && _jsx("span", { style: { marginLeft: 4, fontSize: 10, opacity: 0.7 }, children: "(Paid)" })] }, breakType.name))) })] })), _jsxs("div", { style: {
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 12,
                        paddingTop: 16,
                        borderTop: '1px solid #374151',
                    }, children: [_jsx("button", { onClick: handleClose, disabled: showLoading, style: {
                                padding: '10px 20px',
                                borderRadius: 6,
                                border: '1px solid #374151',
                                backgroundColor: 'transparent',
                                color: '#D1D5DB',
                                cursor: showLoading ? 'not-allowed' : 'pointer',
                                fontSize: 13,
                                fontWeight: 500,
                            }, children: "Cancel" }), _jsxs("button", { onClick: handleConfirm, disabled: showLoading || !canConfirm, style: {
                                padding: '10px 20px',
                                borderRadius: 6,
                                border: 'none',
                                backgroundColor: canConfirm && !showLoading ? '#3B82F6' : '#374151',
                                color: canConfirm && !showLoading ? '#FFFFFF' : '#6B7280',
                                cursor: showLoading || !canConfirm ? 'not-allowed' : 'pointer',
                                fontSize: 13,
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                            }, children: [showLoading && (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", style: {
                                        animation: 'spin 1s linear infinite',
                                    }, children: [_jsx("path", { d: "M21 12a9 9 0 11-18 0 9 9 0 0118 0z", opacity: "0.25" }), _jsx("path", { d: "M21 12a9 9 0 00-9-9" }), _jsx("style", { children: `
                  @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                  }
                ` })] })), "Confirm"] })] })] }) }));
};
export default ClockOutPromptModal;
